import { useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  CallDispositionResult,
  RecordingParties,
} from "@sales-ops-amplify/shared";
import {
  getCallConfig,
  removeDisposition,
  removePurpose,
  removeRecordingRule,
  removeScorecardCriterion,
  removeVoicemailDrop,
  saveDisposition,
  savePurpose,
  saveRecordingRule,
  saveScorecardCriterion,
  saveVoicemailDrop,
  updateDialerPreferences,
} from "@/api/call-config";
import {
  listPhoneNumbers,
  purchaseTwilioNumber,
  searchTwilioNumbers,
  setDefaultPhoneNumber,
  syncTwilioPhoneNumbers,
} from "@/api/phone-numbers";
import {
  SettingsField,
  SettingsInput,
  SettingsPageHeader,
  SettingsSection,
  SettingsTable,
  SettingsToggleRow,
} from "@/components/settings/settings-ui";

function DialerSubnav() {
  const { pathname } = useLocation();
  const items = [
    { href: "/settings/calls", label: "Overview", end: true },
    { href: "/settings/calls/recording", label: "Recording" },
    { href: "/settings/calls/purposes", label: "Purposes" },
    { href: "/settings/calls/dispositions", label: "Dispositions" },
    { href: "/settings/calls/voicemail-drops", label: "Voicemail drops" },
    { href: "/settings/calls/scorecards", label: "Scorecards" },
  ];
  return (
    <div className="mb-6 flex flex-wrap gap-2">
      {items.map((i) => {
        const active =
          i.end === true ? pathname === i.href : pathname.startsWith(i.href);
        return (
          <Link
            key={i.href}
            to={i.href}
            className={
              active
                ? "rounded-md bg-brand/10 px-2.5 py-1 text-sm font-medium text-brand"
                : "rounded-md px-2.5 py-1 text-sm text-muted-foreground hover:bg-surface-hover"
            }
          >
            {i.label}
          </Link>
        );
      })}
    </div>
  );
}

export function SettingsPhoneNumbersPage() {
  const qc = useQueryClient();
  const [message, setMessage] = useState<string | null>(null);
  const [areaCode, setAreaCode] = useState("");
  const [bridgeNumber, setBridgeNumber] = useState("");
  const [searchResults, setSearchResults] = useState<
    Array<{ e164: string; friendlyName?: string; region?: string }>
  >([]);

  const numbersQuery = useQuery({
    queryKey: ["phone-numbers"],
    queryFn: listPhoneNumbers,
  });
  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
    select: (d) => {
      return d;
    },
  });

  const prefs = configQuery.data?.config.preferences;

  const syncMut = useMutation({
    mutationFn: syncTwilioPhoneNumbers,
    onSuccess: () => {
      setMessage("Synced Twilio numbers.");
      qc.invalidateQueries({ queryKey: ["phone-numbers"] });
    },
    onError: (e: unknown) =>
      setMessage(e instanceof Error ? e.message : "Sync failed"),
  });

  const defaultMut = useMutation({
    mutationFn: setDefaultPhoneNumber,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["phone-numbers"] }),
  });

  const prefsMut = useMutation({
    mutationFn: updateDialerPreferences,
    onSuccess: () => {
      setMessage("Preferences saved.");
      qc.invalidateQueries({ queryKey: ["call-config"] });
    },
  });

  const searchMut = useMutation({
    mutationFn: () => searchTwilioNumbers({ country: "US", areaCode }),
    onSuccess: (res) => setSearchResults(res.results),
    onError: (e: unknown) =>
      setMessage(e instanceof Error ? e.message : "Search failed"),
  });

  const buyMut = useMutation({
    mutationFn: purchaseTwilioNumber,
    onSuccess: () => {
      setMessage("Number purchased.");
      qc.invalidateQueries({ queryKey: ["phone-numbers"] });
    },
    onError: (e: unknown) =>
      setMessage(e instanceof Error ? e.message : "Purchase failed"),
  });

  const numbers = numbersQuery.data?.numbers ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Phone numbers"
        description="Caller IDs for the dialer. Sync or purchase Twilio numbers."
        onSave={() =>
          prefsMut.mutate({
            outboundMode: prefs?.outboundMode ?? "voip",
            bridgeNumber: bridgeNumber || prefs?.bridgeNumber,
            showConnectionErrors: prefs?.showConnectionErrors ?? false,
          })
        }
      />
      {message && (
        <p className="mb-4 text-sm text-muted-foreground">{message}</p>
      )}
      <SettingsSection
        title="Caller IDs"
        description="Numbers used for outbound caller ID and inbound routing."
      >
        <SettingsTable
          columns={["Number", "Label", "Default", "Actions"]}
          rows={numbers.map((n) => ({
            Number: n.e164,
            Label: n.label ?? "—",
            Default: n.isDefault ? "Yes" : "—",
            Actions: n.isDefault ? (
              "Default"
            ) : (
              <button
                type="button"
                className="text-brand underline"
                onClick={() => defaultMut.mutate(n.phoneNumberId)}
              >
                Set default
              </button>
            ),
          }))}
          empty={
            numbersQuery.data?.twilioConfigured
              ? "No numbers yet. Sync from Twilio or purchase below."
              : "Twilio is not configured. Add TWILIO_* env vars, then sync."
          }
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded-md border border-border px-3 py-2 text-sm"
            onClick={() => syncMut.mutate()}
          >
            Sync from Twilio
          </button>
        </div>
      </SettingsSection>

      <SettingsSection
        title="Personal dialer preferences"
        description="VoIP (browser) or Bridge (mobile). Bridge is intended for US numbers."
      >
        <SettingsField label="Outgoing call mode">
          <select
            className="w-full max-w-xs rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={prefs?.outboundMode ?? "voip"}
            onChange={(e) =>
              prefsMut.mutate({
                outboundMode: e.target.value as "voip" | "bridge",
              })
            }
          >
            <option value="voip">VoIP (browser)</option>
            <option value="bridge">Bridge (personal phone)</option>
          </select>
        </SettingsField>
        <SettingsField label="Bridge personal number">
          <SettingsInput
            value={bridgeNumber || prefs?.bridgeNumber || ""}
            onChange={(e) => setBridgeNumber(e.target.value)}
            placeholder="+1…"
          />
        </SettingsField>
        <SettingsToggleRow
          label="Show call connection errors (debug)"
          checked={prefs?.showConnectionErrors ?? false}
          onChange={(v) => prefsMut.mutate({ showConnectionErrors: v })}
        />
      </SettingsSection>

      <SettingsSection
        title="Purchase number"
        description="Search available Twilio local numbers by area code."
      >
        <div className="flex flex-wrap gap-2">
          <SettingsInput
            value={areaCode}
            onChange={(e) => setAreaCode(e.target.value)}
            placeholder="Area code (e.g. 415)"
          />
          <button
            type="button"
            className="rounded-md border border-border px-3 py-2 text-sm"
            onClick={() => searchMut.mutate()}
          >
            Search
          </button>
        </div>
        <ul className="mt-3 space-y-1 text-sm">
          {searchResults.map((r) => (
            <li
              key={r.e164}
              className="flex items-center justify-between gap-2 rounded-md border border-border px-2 py-1.5"
            >
              <span>
                {r.e164}{" "}
                <span className="text-muted-foreground">
                  {r.region ?? r.friendlyName}
                </span>
              </span>
              <button
                type="button"
                className="text-brand underline"
                onClick={() => buyMut.mutate(r.e164)}
              >
                Buy
              </button>
            </li>
          ))}
        </ul>
      </SettingsSection>
    </div>
  );
}

export function SettingsTeamDialerPage() {
  return (
    <div>
      <SettingsPageHeader
        title="Team dialer"
        description="Shared dialer settings: recording rules, purposes, dispositions, and voicemail drops."
      />
      <DialerSubnav />
      <SettingsSection title="Quick links">
        <ul className="list-inside list-disc space-y-1 text-sm">
          <li>
            <Link className="text-brand underline" to="/settings/calls/recording">
              Location-based recording rules
            </Link>
          </li>
          <li>
            <Link className="text-brand underline" to="/settings/calls/purposes">
              Call purposes
            </Link>
          </li>
          <li>
            <Link
              className="text-brand underline"
              to="/settings/calls/dispositions"
            >
              Call dispositions
            </Link>
          </li>
          <li>
            <Link
              className="text-brand underline"
              to="/settings/calls/voicemail-drops"
            >
              Voicemail drops
            </Link>
          </li>
          <li>
            <Link className="text-brand underline" to="/settings/account/voices">
              Phone numbers & personal prefs
            </Link>
          </li>
        </ul>
      </SettingsSection>
    </div>
  );
}

export function SettingsDialerRecordingPage() {
  const qc = useQueryClient();
  const [country, setCountry] = useState("US");
  const [states, setStates] = useState("");
  const [inbound, setInbound] = useState<RecordingParties>("none");
  const [outbound, setOutbound] = useState<RecordingParties>("none");
  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });
  const saveMut = useMutation({
    mutationFn: saveRecordingRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["call-config"] }),
  });
  const delMut = useMutation({
    mutationFn: removeRecordingRule,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["call-config"] }),
  });
  const rules = configQuery.data?.config.recordingRules ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Recording"
        description="Location-based rules control who is recorded. Defaults mirror Apollo’s compliance scaffolding — not legal advice."
      />
      <DialerSubnav />
      <SettingsSection title="Rules">
        <SettingsTable
          columns={["Name", "Country", "Inbound", "Outbound", "Actions"]}
          rows={rules.map((r) => ({
            Name: r.name ?? r.id,
            Country:
              r.country +
              (r.states?.length ? ` (${r.states.length} states)` : ""),
            Inbound: r.inboundParties,
            Outbound: r.outboundParties,
            Actions: (
              <button
                type="button"
                className="text-red-600 underline"
                onClick={() => delMut.mutate(r.id)}
              >
                Delete
              </button>
            ),
          }))}
          empty="No rules."
        />
      </SettingsSection>
      <SettingsSection title="New rule">
        <div className="grid max-w-lg gap-2">
          <SettingsField label="Country code">
            <SettingsInput
              value={country}
              onChange={(e) => setCountry(e.target.value)}
            />
          </SettingsField>
          <SettingsField label="US states (comma-separated, optional)">
            <SettingsInput
              value={states}
              onChange={(e) => setStates(e.target.value)}
            />
          </SettingsField>
          <SettingsField label="Inbound parties">
            <select
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={inbound}
              onChange={(e) => setInbound(e.target.value as RecordingParties)}
            >
              <option value="both">Both</option>
              <option value="agent">Agent only</option>
              <option value="prospect">Leads only</option>
              <option value="none">None</option>
            </select>
          </SettingsField>
          <SettingsField label="Outbound parties">
            <select
              className="w-full rounded-md border border-border bg-background px-2 py-1.5 text-sm"
              value={outbound}
              onChange={(e) => setOutbound(e.target.value as RecordingParties)}
            >
              <option value="both">Both</option>
              <option value="agent">Agent only</option>
              <option value="prospect">Leads only</option>
              <option value="none">None</option>
            </select>
          </SettingsField>
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
            onClick={() =>
              saveMut.mutate({
                country,
                states: states
                  ? states
                      .split(",")
                      .map((s) => s.trim())
                      .filter(Boolean)
                  : undefined,
                inboundParties: inbound,
                outboundParties: outbound,
                playAnnouncementInbound: true,
                playAnnouncementOutbound: true,
              })
            }
          >
            Save rule
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}

export function SettingsDialerPurposesPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });
  const saveMut = useMutation({
    mutationFn: savePurpose,
    onSuccess: () => {
      setName("");
      qc.invalidateQueries({ queryKey: ["call-config"] });
    },
  });
  const delMut = useMutation({
    mutationFn: removePurpose,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["call-config"] }),
  });
  const purposes = configQuery.data?.config.purposes ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Call purposes"
        description="Categorize the types of calls your team makes."
      />
      <DialerSubnav />
      <SettingsSection title="Purposes">
        <SettingsTable
          columns={["Name", "Actions"]}
          rows={purposes.map((p) => ({
            Name: p.name,
            Actions: (
              <button
                type="button"
                className="text-red-600 underline"
                onClick={() => delMut.mutate(p.id)}
              >
                Delete
              </button>
            ),
          }))}
          empty="No purposes."
        />
        <div className="mt-3 flex gap-2">
          <SettingsInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Add purpose"
          />
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
            onClick={() => name.trim() && saveMut.mutate({ name: name.trim() })}
          >
            Add
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}

export function SettingsDialerDispositionsPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [result, setResult] =
    useState<CallDispositionResult>("not_connected");
  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });
  const saveMut = useMutation({
    mutationFn: saveDisposition,
    onSuccess: () => {
      setName("");
      qc.invalidateQueries({ queryKey: ["call-config"] });
    },
  });
  const delMut = useMutation({
    mutationFn: removeDisposition,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["call-config"] }),
  });
  const dispositions = configQuery.data?.config.dispositions ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Call dispositions"
        description="Connected stops sequence advance; not connected continues."
      />
      <DialerSubnav />
      <SettingsSection title="Dispositions">
        <SettingsTable
          columns={["Name", "Result", "Sentiment", "Actions"]}
          rows={dispositions.map((d) => ({
            Name: d.name,
            Result: d.result,
            Sentiment: d.sentiment ?? "—",
            Actions: (
              <button
                type="button"
                className="text-red-600 underline"
                onClick={() => delMut.mutate(d.id)}
              >
                Delete
              </button>
            ),
          }))}
          empty="No dispositions."
        />
        <div className="mt-3 flex flex-wrap gap-2">
          <SettingsInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Disposition name"
          />
          <select
            className="rounded-md border border-border bg-background px-2 py-1.5 text-sm"
            value={result}
            onChange={(e) =>
              setResult(e.target.value as CallDispositionResult)
            }
          >
            <option value="connected">Connected</option>
            <option value="not_connected">Not connected</option>
          </select>
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
            onClick={() =>
              name.trim() && saveMut.mutate({ name: name.trim(), result })
            }
          >
            Add
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}

export function SettingsDialerVoicemailPage() {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [audioUrl, setAudioUrl] = useState("");
  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });
  const saveMut = useMutation({
    mutationFn: saveVoicemailDrop,
    onSuccess: () => {
      setLabel("");
      setAudioUrl("");
      qc.invalidateQueries({ queryKey: ["call-config"] });
    },
  });
  const delMut = useMutation({
    mutationFn: removeVoicemailDrop,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["call-config"] }),
  });
  const drops = configQuery.data?.config.voicemailDrops ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Voicemail drops"
        description="HTTPS audio URLs Twilio can Play when no one answers."
      />
      <DialerSubnav />
      <SettingsSection title="Drops">
        <SettingsTable
          columns={["Label", "Audio URL", "Actions"]}
          rows={drops.map((d) => ({
            Label: d.label,
            "Audio URL": d.audioUrl,
            Actions: (
              <button
                type="button"
                className="text-red-600 underline"
                onClick={() => delMut.mutate(d.id)}
              >
                Delete
              </button>
            ),
          }))}
          empty="No voicemail drops."
        />
        <div className="mt-3 grid max-w-lg gap-2">
          <SettingsInput
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Label"
          />
          <SettingsInput
            value={audioUrl}
            onChange={(e) => setAudioUrl(e.target.value)}
            placeholder="https://…/voicemail.mp3"
          />
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
            onClick={() =>
              label.trim() &&
              audioUrl.trim() &&
              saveMut.mutate({
                label: label.trim(),
                audioUrl: audioUrl.trim(),
              })
            }
          >
            Add drop
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}

export function SettingsDialerScorecardsPage() {
  const qc = useQueryClient();
  const [label, setLabel] = useState("");
  const [maxScore, setMaxScore] = useState("5");
  const configQuery = useQuery({
    queryKey: ["call-config"],
    queryFn: getCallConfig,
  });
  const saveMut = useMutation({
    mutationFn: saveScorecardCriterion,
    onSuccess: () => {
      setLabel("");
      setMaxScore("5");
      qc.invalidateQueries({ queryKey: ["call-config"] });
    },
  });
  const delMut = useMutation({
    mutationFn: removeScorecardCriterion,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["call-config"] }),
  });
  const criteria = configQuery.data?.config.scorecardCriteria ?? [];

  return (
    <div>
      <SettingsPageHeader
        title="Call scorecards"
        description="Criteria managers use to coach reps after dialer calls."
      />
      <DialerSubnav />
      <SettingsSection title="Criteria">
        <SettingsTable
          columns={["Label", "Max score", "Actions"]}
          rows={criteria
            .slice()
            .sort((a, b) => a.order - b.order)
            .map((c) => ({
              Label: c.label,
              "Max score": String(c.maxScore),
              Actions: (
                <button
                  type="button"
                  className="text-red-600 underline"
                  onClick={() => delMut.mutate(c.id)}
                >
                  Delete
                </button>
              ),
            }))}
          empty="No scorecard criteria yet."
        />
        <div className="mt-3 grid max-w-lg gap-2">
          <SettingsInput
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="Criterion label"
          />
          <SettingsInput
            value={maxScore}
            onChange={(e) => setMaxScore(e.target.value)}
            placeholder="Max score"
          />
          <button
            type="button"
            className="rounded-md bg-brand px-3 py-2 text-sm text-white"
            onClick={() =>
              label.trim() &&
              saveMut.mutate({
                label: label.trim(),
                maxScore: Number(maxScore) || 5,
              })
            }
          >
            Add criterion
          </button>
        </div>
      </SettingsSection>
    </div>
  );
}
